import CloseIcon from '@mui/icons-material/Close';
import FilterListIcon from '@mui/icons-material/FilterList';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import SearchIcon from '@mui/icons-material/Search';
import {
  Alert,
  Badge,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  FormControl,
  FormHelperText,
  IconButton,
  InputAdornment,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import type { SelectChangeEvent } from '@mui/material/Select';
import FilterTrayMAIQ from 'app/components/ReusableUIComponents/HighLevelComponents/FilterTrayMAIQ';
import type {
  FilterTrayCategory,
  FilterTrayOption,
  FilterTraySelection,
} from 'app/components/ReusableUIComponents/HighLevelComponents/FilterTrayMAIQ';
import type { TestNurturingAutomationStep } from 'app/pages/TestNurturingPage/utils/testNurturingAutomationLeadUtils';
import {
  getNurturingStageOptionLabel,
  getNurturingStageOptionValue,
  getUnsoldLeadDisplayName,
  getUnsoldLeadDisplaySegment,
  getUnsoldLeadRowId,
} from 'app/pages/TestNurturingPage/utils/testNurturingAutomationLeadUtils';

const STEP_CIRCLE_BASE_SX = {
  width: 32,
  height: 32,
  borderRadius: '50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontWeight: 700,
  fontSize: '0.875rem',
};

export type TestNurturingAutomationModalProps = {
  open: boolean;
  onClose: () => void;
  step: TestNurturingAutomationStep;
  onGoToStep2: () => void;
  onBackToStep1: () => void;
  onGoToStep3: () => void;
  onBackToStep2: () => void;
  onFinishAutomation: () => void | Promise<void>;
  startTestingLoading: boolean;
  startTestingError: string | null;
  selectedPrimarySegment: string;
  primarySegmentOptions: string[];
  segmentOptionsLoading: boolean;
  segmentOptionsError: string | null;
  onPrimarySegmentChange: (segment: string) => void;
  isStep1NextDisabled: boolean;
  isStep2NextDisabled: boolean;
  unsoldRows: unknown[];
  unsoldLoading: boolean;
  unsoldFetching: boolean;
  unsoldIsFetchingNextPage: boolean;
  unsoldErrorMessage: string | null;
  unsoldTotalDocuments: number;
  unsoldHasNextPage: boolean;
  onUnsoldListScroll: (e: React.UIEvent<HTMLDivElement>) => void;
  isLeadSelected: (leadId: string) => boolean;
  onToggleLeadSelection: (leadId: string) => void;
  unsoldSearchDraft: string;
  unsoldSearchApplied: string;
  onUnsoldSearchDraftChange: (value: string) => void;
  onApplyUnsoldSearch: () => void;
  unsoldQuickFilterOpen: boolean;
  unsoldQuickFilterAnchorEl: HTMLElement | null;
  unsoldQuickFilterActiveCategoryId: string | null;
  onUnsoldQuickFilterActiveCategoryChange: (categoryId: string | null) => void;
  unsoldQuickFilterCategories: FilterTrayCategory[];
  unsoldQuickFilterOptionsForActiveCategory: FilterTrayOption[];
  unsoldQuickFilterSelectedByCategory: FilterTraySelection;
  onUnsoldQuickFilterSelectionChange: (
    categoryId: string,
    selectedValues: string[],
  ) => void;
  unsoldQuickFilterTotalActive: number;
  onUnsoldQuickFilterClearAll: () => void;
  onUnsoldQuickFilterClearActiveCategory: () => void;
  onUnsoldQuickFilterClearAllInPanel: () => void;
  onOpenUnsoldQuickFilter: (event: React.MouseEvent<HTMLElement>) => void;
  onCloseUnsoldQuickFilter: () => void;
  onClearUnsoldFiltersAndSearch: () => void;
  nurturingStageRows: unknown[];
  nurturingStagesLoading: boolean;
  nurturingStagesError: string | null;
  selectedNurturingStageValue: string;
  onNurturingStageChange: (value: string) => void;
};

export default function TestNurturingAutomationModal({
  open,
  onClose,
  step,
  onGoToStep2,
  onBackToStep1,
  onGoToStep3,
  onBackToStep2,
  onFinishAutomation,
  startTestingLoading,
  startTestingError,
  selectedPrimarySegment,
  primarySegmentOptions,
  segmentOptionsLoading,
  segmentOptionsError,
  onPrimarySegmentChange,
  isStep1NextDisabled,
  isStep2NextDisabled,
  unsoldRows,
  unsoldLoading,
  unsoldFetching,
  unsoldIsFetchingNextPage,
  unsoldErrorMessage,
  unsoldTotalDocuments,
  unsoldHasNextPage,
  onUnsoldListScroll,
  isLeadSelected,
  onToggleLeadSelection,
  unsoldSearchDraft,
  unsoldSearchApplied,
  onUnsoldSearchDraftChange,
  onApplyUnsoldSearch,
  unsoldQuickFilterOpen,
  unsoldQuickFilterAnchorEl,
  unsoldQuickFilterActiveCategoryId,
  onUnsoldQuickFilterActiveCategoryChange,
  unsoldQuickFilterCategories,
  unsoldQuickFilterOptionsForActiveCategory,
  unsoldQuickFilterSelectedByCategory,
  onUnsoldQuickFilterSelectionChange,
  unsoldQuickFilterTotalActive,
  onUnsoldQuickFilterClearAll,
  onUnsoldQuickFilterClearActiveCategory,
  onUnsoldQuickFilterClearAllInPanel,
  onOpenUnsoldQuickFilter,
  onCloseUnsoldQuickFilter,
  onClearUnsoldFiltersAndSearch,
  nurturingStageRows,
  nurturingStagesLoading,
  nurturingStagesError,
  selectedNurturingStageValue,
  onNurturingStageChange,
}: TestNurturingAutomationModalProps) {
  const isStep1 = step === 1;
  const isStep2 = step === 2;
  const isStep3 = step === 3;

  const lineActive12 = step >= 2;
  const lineActive23 = step >= 3;

  const circle1Clickable = isStep2 || isStep3;
  const circle2Clickable =
    (isStep1 && !isStep1NextDisabled) || isStep3;
  const circle3Clickable = isStep2 && !isStep2NextDisabled;

  const handlePrimarySegmentChange = (event: SelectChangeEvent<string>) => {
    onPrimarySegmentChange(event.target.value);
  };

  const handleNurturingStageChange = (event: SelectChangeEvent<string>) => {
    onNurturingStageChange(event.target.value);
  };

  const segmentHelperText = segmentOptionsLoading
    ? 'Loading dealer segments...'
    : segmentOptionsError
      ? segmentOptionsError
      : 'Choose a segment to continue.';

  const canClearUnsoldFiltersRow =
    unsoldQuickFilterTotalActive > 0 ||
    Boolean(unsoldSearchDraft.trim()) ||
    Boolean(unsoldSearchApplied.trim());

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={isStep2 ? 'md' : 'sm'}
      fullWidth
      PaperProps={{
        sx: { borderRadius: 2, maxHeight: '92vh' },
      }}
      aria-labelledby="test-nurturing-automation-dialog-title"
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          px: 2.5,
          pt: 1.5,
          pb: 0.5,
        }}
      >
        <Typography
          id="test-nurturing-automation-dialog-title"
          variant="h6"
          component="h2"
          sx={{ fontWeight: 700, color: 'primary.dark', pr: 1 }}
        >
          Test Nurturing Automation
        </Typography>
        <IconButton
          aria-label="Close"
          onClick={onClose}
          size="small"
          sx={{ color: 'text.secondary', mt: -0.5 }}
        >
          <CloseIcon />
        </IconButton>
      </Box>

      <DialogContent
        sx={{
          pt: 0,
          pb: 1,
          px: 2.5,
          display: 'flex',
          flexDirection: 'column',
          minHeight: 0,
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0,
            mb: 1.5,
            mt: 0,
            flexShrink: 0,
          }}
        >
          <Box
            component={circle1Clickable ? 'button' : 'div'}
            type={circle1Clickable ? 'button' : undefined}
            onClick={circle1Clickable ? onBackToStep1 : undefined}
            aria-label={
              circle1Clickable ? 'Back to step 1 (segment)' : undefined
            }
            sx={{
              ...STEP_CIRCLE_BASE_SX,
              bgcolor: 'primary.main',
              color: 'primary.contrastText',
              border: 'none',
              p: 0,
              font: 'inherit',
              cursor: circle1Clickable ? 'pointer' : 'default',
              ...(circle1Clickable && {
                '&:hover': { transform: 'scale(1.05)', boxShadow: 2 },
                '&:focus-visible': {
                  outline: '2px solid',
                  outlineColor: 'primary.dark',
                  outlineOffset: 2,
                },
              }),
            }}
          >
            1
          </Box>
          <Box
            sx={{
              flex: 1,
              height: 2,
              minWidth: 16,
              bgcolor: lineActive12 ? 'primary.main' : 'divider',
              mx: 0.5,
              transition: 'background-color 0.2s ease',
            }}
          />
          <Box
            component={circle2Clickable ? 'button' : 'div'}
            type={circle2Clickable ? 'button' : undefined}
            onClick={
              isStep1 && !isStep1NextDisabled
                ? () => onGoToStep2()
                : isStep3
                  ? onBackToStep2
                  : undefined
            }
            disabled={
              isStep1 ? isStep1NextDisabled : undefined
            }
            aria-label={
              isStep1
                ? 'Go to step 2'
                : isStep3
                  ? 'Back to step 2'
                  : undefined
            }
            sx={{
              ...STEP_CIRCLE_BASE_SX,
              bgcolor: lineActive12 ? 'primary.main' : 'grey.300',
              color: lineActive12 ? 'primary.contrastText' : 'grey.700',
              border: 'none',
              p: 0,
              font: 'inherit',
              cursor: circle2Clickable ? 'pointer' : 'default',
              ...(circle2Clickable && {
                '&:hover': {
                  transform:
                    isStep1 && isStep1NextDisabled ? 'none' : 'scale(1.05)',
                  boxShadow:
                    isStep1 && isStep1NextDisabled ? 'none' : 2,
                },
                '&:focus-visible': {
                  outline: '2px solid',
                  outlineColor: 'primary.dark',
                  outlineOffset: 2,
                },
                '&:disabled': { cursor: 'not-allowed', opacity: 0.7 },
              }),
            }}
          >
            2
          </Box>
          <Box
            sx={{
              flex: 1,
              height: 2,
              minWidth: 16,
              bgcolor: lineActive23 ? 'primary.main' : 'divider',
              mx: 0.5,
              transition: 'background-color 0.2s ease',
            }}
          />
          <Box
            component={isStep2 ? 'button' : 'div'}
            type={isStep2 ? 'button' : undefined}
            onClick={
              isStep2 && !isStep2NextDisabled ? onGoToStep3 : undefined
            }
            disabled={isStep2 ? isStep2NextDisabled : undefined}
            aria-label={isStep2 ? 'Go to step 3' : undefined}
            sx={{
              ...STEP_CIRCLE_BASE_SX,
              bgcolor: lineActive23 ? 'primary.main' : 'grey.300',
              color: lineActive23 ? 'primary.contrastText' : 'grey.700',
              border: 'none',
              p: 0,
              font: 'inherit',
              cursor: circle3Clickable ? 'pointer' : 'default',
              ...(isStep2 && {
                '&:hover': {
                  transform: isStep2NextDisabled ? 'none' : 'scale(1.05)',
                  boxShadow: isStep2NextDisabled ? 'none' : 2,
                },
                '&:focus-visible': {
                  outline: '2px solid',
                  outlineColor: 'primary.dark',
                  outlineOffset: 2,
                },
                '&:disabled': { cursor: 'not-allowed', opacity: 0.7 },
              }),
            }}
          >
            3
          </Box>
        </Box>

        {isStep1 ? (
          <>
            <Typography
              variant="subtitle1"
              sx={{ fontWeight: 700, mb: 0.5, color: 'text.primary' }}
            >
              Select segment
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ lineHeight: 1.45 }}
            >
              Choose a segment for this test run.
            </Typography>

            {segmentOptionsError ? (
              <Alert severity="warning" sx={{ mt: 1, py: 0.5 }}>
                {segmentOptionsError}
              </Alert>
            ) : null}

            <FormControl
              fullWidth
              sx={{ mt: 1.25 }}
              disabled={segmentOptionsLoading}
              error={Boolean(segmentOptionsError)}
            >
              <Select
                id="test-nurturing-primary-segment-select"
                value={selectedPrimarySegment}
                onChange={handlePrimarySegmentChange}
                displayEmpty
                inputProps={{
                  'aria-label': 'Primary segment',
                }}
                renderValue={selected =>
                  selected === '' || selected == null
                    ? 'Select segment'
                    : String(selected)
                }
              >
                <MenuItem value="">
                  <em>Select segment</em>
                </MenuItem>
                {!segmentOptionsLoading &&
                  primarySegmentOptions.map(segment => (
                    <MenuItem key={segment} value={segment}>
                      {segment}
                    </MenuItem>
                  ))}
              </Select>
              <FormHelperText>
                {segmentOptionsLoading ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CircularProgress size={14} />
                    {segmentHelperText}
                  </Box>
                ) : (
                  segmentHelperText
                )}
              </FormHelperText>
            </FormControl>
          </>
        ) : isStep2 ? (
          <>
            <Typography
              variant="subtitle1"
              sx={{ fontWeight: 700, mb: 0.5, color: 'text.primary' }}
            >
              Select candidates
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ lineHeight: 1.45, mb: 1 }}
            >
              Unsold leads for this dealer. Use quick filters (segment name) or
              search name or segment. Select at least one lead to continue. Scroll the
              list to load more.
            </Typography>

            <Stack
              direction="row"
              alignItems="center"
              spacing={1.5}
              useFlexGap
              sx={{
                width: '100%',
                minWidth: 0,
                mb: 1,
                flexWrap: 'nowrap',
              }}
            >
              <Box
                sx={{
                  flexShrink: 0,
                  display: 'flex',
                  alignItems: 'center',
                  height: 40,
                }}
              >
                <Badge
                  badgeContent={
                    unsoldQuickFilterTotalActive > 0
                      ? unsoldQuickFilterTotalActive
                      : undefined
                  }
                  color="primary"
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    '& .MuiBadge-badge': { fontSize: 10 },
                  }}
                >
                  <Button
                    size="small"
                    variant="outlined"
                    color="primary"
                    startIcon={<FilterListIcon />}
                    onClick={onOpenUnsoldQuickFilter}
                    sx={{
                      textTransform: 'none',
                      whiteSpace: 'nowrap',
                      height: 40,
                      minHeight: 40,
                      boxSizing: 'border-box',
                    }}
                  >
                    Quick Filters
                  </Button>
                </Badge>
                <FilterTrayMAIQ
                  isLoading={unsoldLoading || unsoldFetching}
                  open={unsoldQuickFilterOpen}
                  onClose={onCloseUnsoldQuickFilter}
                  anchorEl={unsoldQuickFilterAnchorEl}
                  categories={unsoldQuickFilterCategories}
                  activeCategoryId={unsoldQuickFilterActiveCategoryId}
                  onActiveCategoryChange={onUnsoldQuickFilterActiveCategoryChange}
                  optionsForActiveCategory={
                    unsoldQuickFilterOptionsForActiveCategory
                  }
                  selectedByCategory={unsoldQuickFilterSelectedByCategory}
                  onSelectionChange={onUnsoldQuickFilterSelectionChange}
                  totalActiveCount={unsoldQuickFilterTotalActive}
                  onClearAllFilters={onUnsoldQuickFilterClearAll}
                  onClearActiveCategory={onUnsoldQuickFilterClearActiveCategory}
                  onClearAllInPanel={onUnsoldQuickFilterClearAllInPanel}
                />
              </Box>
              <TextField
                size="small"
                sx={{
                  flex: 1,
                  minWidth: 0,
                  '& .MuiOutlinedInput-root': {
                    height: 40,
                    boxSizing: 'border-box',
                  },
                }}
                placeholder="search name or segment"
                value={unsoldSearchDraft}
                onChange={e => onUnsoldSearchDraftChange(e.target.value)}
                onKeyDown={e => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    onApplyUnsoldSearch();
                  }
                }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        size="small"
                        aria-label="Apply search"
                        edge="end"
                        onClick={onApplyUnsoldSearch}
                      >
                        <SearchIcon fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              <Tooltip title="Clear all filters" arrow enterDelay={400}>
                <Box
                  component="span"
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    height: 40,
                    flexShrink: 0,
                  }}
                >
                  <IconButton
                    aria-label="Clear all filters"
                    onClick={onClearUnsoldFiltersAndSearch}
                    disabled={!canClearUnsoldFiltersRow}
                    color="primary"
                    sx={{
                      flexShrink: 0,
                      height: 40,
                      width: 40,
                      boxSizing: 'border-box',
                      border: '1px solid',
                      borderColor: 'primary.main',
                      borderRadius: 1,
                      bgcolor: 'background.paper',
                      '&:hover': {
                        bgcolor: 'action.hover',
                        borderColor: 'primary.dark',
                      },
                      '&.Mui-disabled': {
                        borderColor: 'action.disabledBackground',
                        color: 'action.disabled',
                        bgcolor: 'background.paper',
                      },
                    }}
                  >
                    <RestartAltIcon sx={{ fontSize: 22 }} />
                  </IconButton>
                </Box>
              </Tooltip>
            </Stack>

            {unsoldFetching ? (
              <LinearProgress sx={{ mb: 0.5, borderRadius: 1 }} />
            ) : (
              <Box sx={{ height: 2, mb: 0.5 }} />
            )}

            {unsoldErrorMessage ? (
              <Alert severity="error" sx={{ mb: 0.5, py: 0.25 }}>
                {unsoldErrorMessage}
              </Alert>
            ) : null}

            <Box
              sx={{
                flex: 1,
                minHeight: 140,
                maxHeight: { xs: 400, sm: 480 },
                overflow: 'auto',
                pr: 0.5,
              }}
              onScroll={onUnsoldListScroll}
            >
              {unsoldLoading && !unsoldRows.length ? (
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    py: 3,
                  }}
                >
                  <CircularProgress />
                </Box>
              ) : unsoldRows.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No unsold leads found.
                </Typography>
              ) : (
                <Stack spacing={0.75}>
                  {unsoldRows.map(row => {
                    const id = getUnsoldLeadRowId(row);
                    if (!id) return null;
                    const name = getUnsoldLeadDisplayName(row);
                    const segmentLabel = getUnsoldLeadDisplaySegment(row);
                    return (
                      <Paper
                        key={id}
                        variant="outlined"
                        sx={{
                          p: 1,
                          display: 'flex',
                          flexDirection: 'row',
                          alignItems: 'center',
                          gap: 1,
                          borderRadius: 1,
                        }}
                      >
                        <Checkbox
                          size="small"
                          checked={isLeadSelected(id)}
                          onChange={() => onToggleLeadSelection(id)}
                          inputProps={{ 'aria-label': `Select ${name}` }}
                          sx={{ p: 0.25, m: -0.25 }}
                        />
                        <Box sx={{ minWidth: 0, py: 0.125 }}>
                          <Typography
                            variant="body2"
                            sx={{ fontWeight: 600, lineHeight: 1.35 }}
                          >
                            {name}
                          </Typography>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ display: 'block', lineHeight: 1.35, mt: 0.125 }}
                          >
                            {segmentLabel}
                          </Typography>
                        </Box>
                      </Paper>
                    );
                  })}
                  {unsoldIsFetchingNextPage ? (
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        py: 1,
                      }}
                    >
                      <CircularProgress size={22} />
                    </Box>
                  ) : null}
                </Stack>
              )}
            </Box>

            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.75, display: 'block' }}>
              {unsoldTotalDocuments > 0
                ? `Showing ${unsoldRows.length} of ${unsoldTotalDocuments}${
                    unsoldHasNextPage ? ' · scroll down to load more' : ''
                  }`
                : '0 results'}
            </Typography>
          </>
        ) : (
          <>
            <Typography
              variant="subtitle1"
              sx={{ fontWeight: 700, mb: 0.5, color: 'text.primary' }}
            >
              Select nurturing stage
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ lineHeight: 1.45, mb: 1 }}
            >
              Pick a stage from the dealer nurturing journey. This list comes
              from the nurturing journey stages API.
            </Typography>

            {nurturingStagesError ? (
              <Alert severity="error" sx={{ mb: 1, py: 0.5 }}>
                {nurturingStagesError}
              </Alert>
            ) : null}

            {startTestingError ? (
              <Alert severity="error" sx={{ mb: 1, py: 0.5 }}>
                {startTestingError}
              </Alert>
            ) : null}

            <FormControl
              fullWidth
              disabled={nurturingStagesLoading || startTestingLoading}
              error={Boolean(nurturingStagesError)}
            >
              <Select
                id="test-nurturing-nurturing-stage-select"
                value={selectedNurturingStageValue}
                onChange={handleNurturingStageChange}
                displayEmpty
                inputProps={{
                  'aria-label': 'Nurturing stage',
                }}
                renderValue={selected => {
                  if (selected === '' || selected == null) {
                    return (
                      <Typography component="span" color="text.secondary">
                        {nurturingStagesLoading
                          ? 'Loading stages...'
                          : 'Select a stage'}
                      </Typography>
                    );
                  }
                  const idx = nurturingStageRows.findIndex(
                    (row, i) =>
                      getNurturingStageOptionValue(row, i) === selected,
                  );
                  if (idx < 0) return String(selected);
                  return getNurturingStageOptionLabel(
                    nurturingStageRows[idx],
                    idx,
                  );
                }}
              >
                {!nurturingStagesLoading &&
                  nurturingStageRows.map((row, index) => {
                    const value = getNurturingStageOptionValue(row, index);
                    const label = getNurturingStageOptionLabel(row, index);
                    return (
                      <MenuItem key={`${value}-${index}`} value={value}>
                        {label}
                      </MenuItem>
                    );
                  })}
              </Select>
              <FormHelperText>
                {nurturingStagesLoading ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CircularProgress size={14} />
                    Loading nurturing stages...
                  </Box>
                ) : nurturingStageRows.length === 0 ? (
                  'No stages returned for this dealer.'
                ) : (
                  `${nurturingStageRows.length} stage(s) available`
                )}
              </FormHelperText>
            </FormControl>
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 2.5, pb: 1.25, pt: 0 }}>
        {isStep3 ? (
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            width="100%"
            spacing={1.5}
          >
            <Button
              variant="outlined"
              color="inherit"
              disabled={startTestingLoading}
              onClick={onBackToStep2}
            >
              Back
            </Button>
            <Button
              variant="contained"
              color="primary"
              disabled={
                !selectedNurturingStageValue?.trim() || startTestingLoading
              }
              onClick={() => void onFinishAutomation()}
              startIcon={
                startTestingLoading ? (
                  <CircularProgress size={18} color="inherit" />
                ) : undefined
              }
            >
              Start Testing
            </Button>
          </Stack>
        ) : isStep2 ? (
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            width="100%"
            spacing={1.5}
          >
            <Button variant="outlined" color="inherit" onClick={onBackToStep1}>
              Back
            </Button>
            <Button
              variant="contained"
              color="primary"
              disabled={isStep2NextDisabled}
              onClick={onGoToStep3}
            >
              Next
            </Button>
          </Stack>
        ) : (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-end',
              width: '100%',
            }}
          >
            <Button
              variant="contained"
              color="primary"
              disabled={isStep1NextDisabled}
              onClick={() => onGoToStep2()}
            >
              Next
            </Button>
          </Box>
        )}
      </DialogActions>
    </Dialog>
  );
}
